import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import BookedRentalsView from './BookedRentalsView/';
import ActiveRentalsView from './ActiveRentalsView';
import {
	setRentalsActiveTab,
	isBookingsViewExpiredChecked,
	resetFilterStatus,
	isBookingsViewDeliveryChecked,
} from 'actions';
import { Routes, useTypedLocation } from 'routing';
import SetPageTitle from 'components/Portals/SetPageTitle';

import LocationTypeSelect from 'components/LocationTypeSelect';

import {
	buildRentalsOfTypeAtSelectedLocationCount,
	buildServiceRentalsOfTypeAtSelectedLocation,
	RentalsType,
	LocationType,
} from 'selectors/RentalsSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ViewActions from 'actions/ViewActions';
import { RentalsTabValue } from 'services/types';
import { useTranslation } from 'services/localization/useTranslation';

import { Box, Button, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';

import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';

import DeliveryExportModal from './DeliveryExportModal';
import { RiArrowDownSFill, RiDownload2Line } from 'react-icons/ri';
import TabContainer from 'components/nav/TabContainer';
import Container from 'components/Container';
import { useHasFeature } from 'hooks';
import CompletedBookingsView from './CompletedBookingsView';
import PendingBookingsView from './PendingBookingsView';
import SetPageNavAction from 'components/Portals/SetPageNavAction';

const bookedRentalCountSelector = buildRentalsOfTypeAtSelectedLocationCount(
	RentalsType.BOOKED,
	LocationType.START,
);

const activeRentalCountSelector = buildRentalsOfTypeAtSelectedLocationCount(
	RentalsType.ACTIVE,
	LocationType.END,
);

const pendingRentalsCountSelector = buildRentalsOfTypeAtSelectedLocationCount(
	RentalsType.PENDING,
	LocationType.START,
);

const bookedServiceRentals = buildServiceRentalsOfTypeAtSelectedLocation(
	RentalsType.BOOKED,
	LocationType.START,
);

const activeServiceRentals = buildServiceRentalsOfTypeAtSelectedLocation(
	RentalsType.ACTIVE,
	LocationType.START,
);

const RentalsRouter = () => {
	const location = useLocation();
	const dispatch = useDispatch();
	const classes = useStyles();
	const theme = useTheme();
	const { t } = useTranslation();
	const { hasFeature } = useHasFeature();
	const { state } = useTypedLocation();
	const rentalsActiveTab = useSelector(ViewSelectors.rentalsActiveTab);
	const bookedRentalsCount = useSelector(bookedRentalCountSelector);
	const activeRentalsCount = useSelector(activeRentalCountSelector);
	const pendingRentalsCount = useSelector(pendingRentalsCountSelector);
	const shopLocationsCount = useSelector(ShopSelectors.allShopLocationsCount);
	const bookedDeliveriesCount = useSelector(bookedServiceRentals).filter(
		(rental) => rental.services?.delivery,
	).length;
	const activeDeliveriesCount = useSelector(activeServiceRentals).filter(
		(rental) => rental.services?.delivery,
	).length;
	const hasOngoingDeliveries = bookedDeliveriesCount > 0 || activeDeliveriesCount > 0;
	const bookingsViewLocationType = useSelector(ViewSelectors.bookingsViewLocationType);
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const [anchorEl, setAnchorEl] = useState<any>(null);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

	useEffect(() => {
		const activeTabValue = location.pathname
			.replace(Routes.BOOKINGS, '')
			.replace(/\//g, '')
			.toUpperCase() as RentalsTabValue;
		if (activeTabValue !== rentalsActiveTab) {
			dispatch(resetFilterStatus());
			dispatch(isBookingsViewDeliveryChecked(false));
		}
		if (activeTabValue !== 'BOOKED') {
			dispatch(isBookingsViewExpiredChecked(false));
		}
		dispatch(setRentalsActiveTab(activeTabValue));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	const handleClickExport = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseExport = () => {
		setAnchorEl(null);
	};

	const handleLocationTypeChange = (type: 'all' | 'current') =>
		dispatch(ViewActions.setBookingsViewLocationType(type));

	const renderExportButton = () => {
		return isMobile ? (
			<Box className={classes.exportIcon} onClick={() => setIsModalOpen(true)}>
				<RiDownload2Line />
			</Box>
		) : (
			<div>
				<Button onClick={(e) => handleClickExport(e)} className={classes.exportButton}>
					{t('delivery.export.export', 'Export')}
					<RiArrowDownSFill />
				</Button>
				<Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseExport}>
					<MenuItem
						onClick={() => {
							setIsModalOpen(true);
							handleCloseExport();
						}}
					>
						{t('delivery.export.deliveryRoutes', 'Delivery routes')}
					</MenuItem>
				</Menu>
			</div>
		);
	};

	return (
		<>
			<SetPageTitle
				title={t('headerBar.orders', 'Orders')}
				priority={2}
				rightContent={
					<Box className={classes.rightContentWrapper}>
						{shopLocationsCount > 1 && hasFeature('BOOKINGS_VIEW_ALL_LOCATIONS_FILTER') ? (
							<LocationTypeSelect
								locationType={bookingsViewLocationType}
								onLocationTypeChange={handleLocationTypeChange}
							/>
						) : undefined}
						{hasOngoingDeliveries && renderExportButton()}
					</Box>
				}
				styling={{ fluid: true }}
			/>
			{state?.from === Routes.HOME_VIEW && (
				<SetPageNavAction
					priority={2}
					backAction={{
						to: Routes.HOME_VIEW,
						title: t('common:actions.home', 'Home'),
					}}
					quickLinks={[]}
				/>
			)}
			<DeliveryExportModal
				isModalOpen={isModalOpen}
				onClose={setIsModalOpen}
				bookingsViewLocatioType={bookingsViewLocationType}
			/>
			<TabContainer
				basePath={Routes.BOOKINGS}
				tabs={[
					{
						path: 'booked',
						label: t('rentals.upcoming', 'Upcoming'),
						component: BookedRentalsView,
						badge: bookedRentalsCount,
					},
					{
						path: 'active',
						label: t('rentals.active', 'Active'),
						component: ActiveRentalsView,
						badge: activeRentalsCount,
					},
					{
						path: 'pending',
						label: t('bookingStates.pending', 'Pending'),
						component: PendingBookingsView,
						badge: pendingRentalsCount,
					},
					{
						path: 'completed',
						label: t('rentals.completed', 'Completed'),
						component: CompletedBookingsView,
					},
				]}
				defaultTab="booked"
				renderTabs={(tabs) => (
					<Container noBottomPadding fluid>
						{tabs}
					</Container>
				)}
			/>
		</>
	);
};

const useStyles = makeStyles((theme) =>
	createStyles({
		rightContentWrapper: {
			display: 'flex',
			flexFlow: 'row',
			justifyContent: 'center',
			alignContent: 'center',
		},
		exportIcon: {
			background: theme.palette.background.secondary,
			borderRadius: 50,
			padding: theme.spacing(1),
		},
		exportButton: {
			fontWeight: 500,
			'&:hover': {
				backgroundColor: 'transparent',
			},
		},
	}),
);
export default RentalsRouter;
